import React from 'react'
import { X, Copy, CheckCircle, AlertTriangle, DollarSign } from 'lucide-react'

interface PaymentConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  isLoading: boolean
  error: string | null
  paymentData: {
    amount: number
    reference: string
    caseNumber: string
    dueDate: string
  }
  bankAccount: {
    accountNumber: string
    accountHolderName: string
    bankName: string
  }
}

export default function PaymentConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
  paymentData,
  bankAccount
}: PaymentConfirmationModalProps) {
  const [copiedItem, setCopiedItem] = React.useState<string | null>(null)

  const handleCopy = (text: string, item: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedItem(item)
      setTimeout(() => setCopiedItem(null), 2000)
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR'
    }).format(amount)
  }

  const formatAccountNumber = (accountNumber: string) => {
    // Format account number with spaces for better readability
    return accountNumber.replace(/(\d{4})(?=\d)/g, '$1 ')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-900">Confirm Bank Transfer</h2>
                <p className="text-sm text-neutral-600">Review payment details and confirm to proceed</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-600 transition-colors p-1"
              disabled={isLoading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Summary */}
          <div className="bg-neutral-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Payment Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Amount:</span>
                <span className="font-semibold text-lg text-neutral-900">{formatCurrency(paymentData.amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Reference:</span>
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-sm bg-white px-2 py-1 rounded border border-neutral-200">
                    {paymentData.reference}
                  </span>
                  <button
                    onClick={() => handleCopy(paymentData.reference, 'reference')}
                    className={`p-1 rounded transition-colors ${
                      copiedItem === 'reference'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-neutral-100 text-neutral-600'
                    }`}
                    title="Copy reference"
                  >
                    {copiedItem === 'reference' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Case Number:</span>
                <span className="font-mono text-sm">{paymentData.caseNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Due Date:</span>
                <span className="text-sm">
                  {new Date(paymentData.dueDate).toLocaleDateString('en-MY', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Bank Account Details */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Bank Account Details</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-neutral-600">Bank:</span>
                <div className="font-medium text-neutral-900">{bankAccount.bankName}</div>
              </div>
              <div>
                <span className="text-sm text-neutral-600">Account Holder:</span>
                <div className="font-medium text-neutral-900">{bankAccount.accountHolderName}</div>
              </div>
              <div>
                <span className="text-sm text-neutral-600">Account Number:</span>
                <div className="flex items-center space-x-2">
                  <span className="font-mono text-lg bg-white px-3 py-2 rounded border border-blue-200">
                    {formatAccountNumber(bankAccount.accountNumber)}
                  </span>
                  <button
                    onClick={() => handleCopy(bankAccount.accountNumber, 'accountNumber')}
                    className={`p-2 rounded transition-colors ${
                      copiedItem === 'accountNumber'
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-blue-100 text-blue-600'
                    }`}
                    title="Copy account number"
                  >
                    {copiedItem === 'accountNumber' ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Transfer Instructions */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Transfer Instructions</h3>
            <ol className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">1.</span>
                <span>Log in to your online banking or mobile banking app</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">2.</span>
                <span>Select "Fund Transfer" or "Interbank Transfer"</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">3.</span>
                <span>Enter the account number and amount shown above</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">4.</span>
                <span>Include the reference number in the payment description/remark</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">5.</span>
                <span>Complete the transfer and save the transaction receipt</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="font-semibold text-amber-800">6.</span>
                <span>Upload the receipt in the next step for verification</span>
              </li>
            </ol>
          </div>

          {/* Important Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Important Note</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Please ensure the reference number is included in your transfer. This helps us identify your payment quickly.
                  Payments without the correct reference may be delayed or not processed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-neutral-200 p-6">
          <div className="flex space-x-3 justify-end">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              {isLoading ? 'Creating Payment...' : 'Confirm Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}